import type { LoadingValue, ProductTileProps } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import type ProductTileItem from '@modules/tile/type/product-tile-item';
import getProductTileProps from '@modules/tile/util/get-product-tile-props';
import type { FavouriteItem } from '~/modules/design-board/type/design-board-types';
import ShoppingAvailable from './ShoppingAvailable';
import type { SyntheticEvent } from 'react';
import VariantDot from './VariantDot';
import getProductPriceDisplayProps from '../../util/get-product-tile-price-display-props';
import ProductTile from './ProductImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { getExtraVariantCount } from '~/modules/product/util/get-extra-variant-count';

export interface ProductTileData {
    Product: ProductTileItem;
    Professional: {
        ID: number;
        Title: string;
        Link: string;
    };
}

interface TileProps
    extends Pick<
        ProductTileProps,
        'size' | 'variant' | 'variables' | 'topActions' | 'adminCaption'
    > {
    data: ProductTileData;
    favouriteItem: FavouriteItem;
    imgLoading?: LoadingValue;
    hideFavourite?: boolean;
    onClick?: (e: SyntheticEvent) => void;
}

const ProductTileWrapper = ({
    data,
    size,
    variant,
    favouriteItem,
    imgLoading = 'lazy',
    onClick,
    ...rest
}: TileProps) => {
    const { mobile } = useAppDisplayModeContext();
    const {
        productName,
        productLink,
        imageUrl,
        professional,
        variantColors,
        variantColorCount,
        mixBlendMode,
    } = getProductTileProps(data);

    const { pricePrefix, price } = getProductPriceDisplayProps(
        data.Product.PricingDisplay
    );

    const productID = data.Product.ID as number;
    const canPurchase = data.Product.CanPurchase;

    return (
        <ProductTile
            key={data.Product.ID}
            data-testid="product-tile"
            as={Link}
            prefetch="intent"
            to={productLink}
            {...(imageUrl
                ? {
                      image: [
                          {
                              src: imageUrl,
                              width: '100%',
                              height: '100%',
                              layout: 'responsive',
                              mixBlendMode: mixBlendMode,
                              loading: imgLoading,
                              sizes: getImagesSizes(348),
                              alt: productName,
                          },
                      ],
                  }
                : { image: [] })}
            professionalName={{
                children: `By ${professional}`,
                maximumLines: 2,
                style: {
                    textTransform: 'none',
                },
            }}
            productName={{
                children: productName,
                maximumLines: 2,
            }}
            pricePrefix={pricePrefix}
            price={price}
            variants={variantColors.map(({ ID, Dot, Label, Code }) => ({
                key: `variant-dot-${ID}`,
                children: (
                    <VariantDot
                        size={mobile ? 9 : 13}
                        colorCode={Code ?? ''}
                        description={Label}
                        image={Dot?.[mobile ? 'Mobile' : 'Desktop']}
                    />
                ),
                'data-testid': `variant-color-${ID}`,
            }))}
            additionalVariantCount={getExtraVariantCount(variantColorCount)}
            onFavourite={(e) => favouriteItem.onClick(e, productID)}
            isFavourited={favouriteItem.favourited(productID)}
            size={size}
            variant={variant}
            newDesignEnabled={true}
            topContent={
                canPurchase
                    ? [
                          <ShoppingAvailable
                              key="shopping-available"
                              floating={'left'}
                          />,
                      ]
                    : []
            }
            onClick={onClick}
            {...rest}
            impression={{
                professionalID: data.Professional.ID,
                itemID: productID,
                type: 'LibraryItem_Directory',
            }}
        />
    );
};

export default ProductTileWrapper;
